import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import {
  useDocument,
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import {
  FieldHeader,
  FieldLabel,
  FieldSubLabel,
  FieldHelpText,
} from './FieldParts';
import { Input } from '../ui/input';
import { cn } from '../lib/utils';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { DOCUMENT_RESPONSE_SET_QUERY } from './queries';

interface Props {
  element: DocumentElementType;
}

interface ResponseOption {
  name: string;
  colour: string;
  score?: number | null;
  flagged?: boolean | null;
}

/** Sentinel name representing the "Other" pill in `selected[]`. */
const OTHER_VALUE = '__OTHER__';
/** Sentinel name representing the "None" pill in `selected[]`. */
const NONE_VALUE = '__NONE__';
/** Default neutral colour used for the Other and None pills in editor preview. */
const OTHER_COLOUR = '#9CA3AF';
const NONE_COLOUR = '#9CA3AF';

/**
 * Small deterministic PRNG (mulberry32). Seeded once per respondent so the
 * shuffled option order stays stable across re-renders within a fill session
 * but differs between viewers — removing position bias without reordering the
 * authored response set.
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher–Yates shuffle driven by the seeded PRNG (pure; returns a new array). */
function seededShuffle<T>(arr: T[], seed: number): T[] {
  const rnd = mulberry32(seed);
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Coerce a configForm value (which may be a string) to a positive int or null. */
const toPositiveInt = (v: unknown): number | null => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
};

/** Canonical filler value shape (legacy reads accepted via normalise). */
type CanonicalValue = { selected: string[]; other?: string };
/**
 * Persisted shape for each picked response. We snapshot the response's
 * score + flagged config at submission time so the answer keeps
 * reporting correctly even if the source response is later renamed or
 * removed from the set. `other` and `none` distinguish the two
 * sentinel pills from row picks.
 */
interface MultiChoiceRichEntry {
  value: string;
  label: string;
  score: number | null;
  flagged: boolean | null;
  other: boolean;
  none: boolean;
}
type RawValue =
  | MultiChoiceRichEntry[]
  | CanonicalValue
  | string
  | string[]
  | undefined
  | null;

/**
 * Pick a contrasting text colour (#fff or #333) for a given hex background.
 * Inline copy of the helper used by other doc elements.
 */
const getContrastColor = (hex: string): string => {
  try {
    const m = hex.replace('#', '').match(/.{1,2}/g);
    if (!m || m.length < 3) return '#333333';
    const [r, g, b] = m.slice(0, 3).map((h) => parseInt(h, 16) / 255);
    const toLin = (c: number) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const luminance = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
    return luminance > 0.4 ? '#333333' : '#FFFFFF';
  } catch {
    return '#333333';
  }
};

const isLocalResponseSetId = (id: string | undefined): boolean =>
  !!id && id.startsWith('local_');

/**
 * Treat a value as a rich entry if it carries the per-entry metadata
 * keys we write today. Lets us tell rich arrays apart from legacy
 * `string[]` answers without false positives.
 */
const isRichEntry = (v: unknown): v is MultiChoiceRichEntry =>
  !!v &&
  typeof v === 'object' &&
  !Array.isArray(v) &&
  typeof (v as MultiChoiceRichEntry).value === 'string' &&
  typeof (v as MultiChoiceRichEntry).other === 'boolean' &&
  typeof (v as MultiChoiceRichEntry).none === 'boolean';

const blankRichEntry = (
  name: string,
  label?: string,
): MultiChoiceRichEntry => ({
  value: name,
  label: label ?? name,
  score: null,
  flagged: null,
  other: false,
  none: false,
});

/**
 * Coerce whatever shape sits in the persisted answer into a stable
 * rich array. Tolerates the pre-rich-entry shapes so saved drafts and
 * legacy server payloads keep loading cleanly.
 */
function toRichEntries(raw: RawValue): MultiChoiceRichEntry[] {
  if (Array.isArray(raw)) {
    return raw.map((entry) => {
      if (isRichEntry(entry)) return entry as MultiChoiceRichEntry;
      if (typeof entry === 'string') return blankRichEntry(entry);
      // Tolerate a partial object entry — the mobile app persists
      // `{ value, label }` (and, for "Other", `{ value: __OTHER__,
      // other: <text> }`) without the boolean other/none/score keys, so
      // it fails `isRichEntry`. Preserve the answer instead of dropping
      // it (which is why mobile picks used to vanish from the web
      // result), mapping the mobile shape onto the canonical rich entry.
      if (
        entry &&
        typeof entry === 'object' &&
        typeof (entry as { value?: unknown }).value === 'string'
      ) {
        const o = entry as { value: string; label?: unknown; other?: unknown };
        if (o.value === NONE_VALUE) {
          return {
            value: '',
            label: 'None',
            score: null,
            flagged: null,
            other: false,
            none: true,
          };
        }
        if (o.value === OTHER_VALUE) {
          return {
            value: typeof o.other === 'string' ? o.other : '',
            label: 'Other',
            score: null,
            flagged: null,
            other: true,
            none: false,
          };
        }
        return blankRichEntry(
          o.value,
          typeof o.label === 'string' ? o.label : undefined,
        );
      }
      return blankRichEntry('');
    });
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    // Legacy `{ selected, other? }` shape: synthesise rich entries.
    const obj = raw as CanonicalValue;
    const out: MultiChoiceRichEntry[] = [];
    const selected = Array.isArray(obj.selected) ? obj.selected : [];
    for (const v of selected) {
      if (v === OTHER_VALUE) {
        out.push({
          value: typeof obj.other === 'string' ? obj.other : '',
          label: 'Other',
          score: null,
          flagged: null,
          other: true,
          none: false,
        });
      } else if (v === NONE_VALUE) {
        out.push({
          value: '',
          label: 'None',
          score: null,
          flagged: null,
          other: false,
          none: true,
        });
      } else {
        out.push(blankRichEntry(v));
      }
    }
    return out;
  }
  if (typeof raw === 'string' && raw !== '') return [blankRichEntry(raw)];
  return [];
}

/**
 * The runtime keeps using `{ selected, other }` for its toggle-group
 * rendering and event handlers — derive that view from the rich
 * entries on each render rather than threading the new shape
 * everywhere.
 */
function richToCanonical(entries: MultiChoiceRichEntry[]): CanonicalValue {
  const out: CanonicalValue = { selected: [] };
  let otherText: string | undefined;
  for (const e of entries) {
    if (e.none) {
      out.selected.push(NONE_VALUE);
    } else if (e.other) {
      out.selected.push(OTHER_VALUE);
      otherText = e.value;
    } else {
      out.selected.push(e.value);
    }
  }
  if (otherText !== undefined) out.other = otherText;
  return out;
}

const MultiChoice: React.FC<Props> = ({ element }) => {
  const [rawValue, setValue] = useElementValue<RawValue>(element.id);
  const { document } = useDocument();
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();

  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    responseSetId?: string;
    /** When true, renders as multi-select; otherwise single-select */
    allowMultiple?: boolean;
    /**
     * Multi-select only: minimum picks required to satisfy `required`.
     * configForm number-input writes strings, so accept either shape.
     */
    minimum?: number | string;
    /** Multi-select only: maximum picks the filler may make (the hard cap). */
    maximum?: number | string;
    /**
     * When true, the option order is randomised per respondent at fill time
     * (not in the editor preview) so no option gets a position-bias advantage.
     */
    shuffleOptions?: boolean;
  };
  const label =
    data?.label ||
    (data?.allowMultiple ? 'Select all that apply' : 'Select an option');
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = data?.required || false;
  // Defensive unwrap — see DropdownSelect comment. A short window of
  // editor builds wrapped configForm field writes in a `{ value }`
  // envelope, which leaked into element.data. Newer writes stay bare;
  // this read tolerates the legacy shape so existing forms load.
  const rawResponseSetId = data?.responseSetId as unknown;
  const responseSetId: string | undefined =
    typeof rawResponseSetId === 'string'
      ? rawResponseSetId
      : rawResponseSetId &&
        typeof rawResponseSetId === 'object' &&
        !Array.isArray(rawResponseSetId) &&
        'value' in (rawResponseSetId as Record<string, unknown>) &&
        Object.keys(rawResponseSetId as Record<string, unknown>).length === 1
      ? ((rawResponseSetId as { value: unknown }).value as string | undefined)
      : undefined;
  const allowMultiple = data?.allowMultiple || false;
  // Selection bounds only apply in multi-select. `minimum` gates `required`;
  // `maximum` is a hard ceiling enforced on every pick.
  const minimum =
    allowMultiple && required ? toPositiveInt(data?.minimum) : null;
  const maximum = allowMultiple ? toPositiveInt(data?.maximum) : null;
  const shuffleOptions = data?.shuffleOptions || false;
  // One seed per mount → stable order within a session, different per viewer.
  const [shuffleSeed] = useState(() => Math.floor(Math.random() * 2 ** 31));

  const richEntries = useMemo(() => toRichEntries(rawValue), [rawValue]);
  const value = useMemo(() => richToCanonical(richEntries), [richEntries]);
  const selectedValues = value.selected;
  const otherText = value.other || '';

  // Resolve the response set from the document content: form-local sets
  // always live there, and the public host inlines the owner's global sets
  // too (an anonymous filler can't query them by id).
  const localSet = useMemo(() => {
    if (!responseSetId) return null;
    return (
      (document.responseSets || []).find((s) => s.id === responseSetId) ??
      null
    );
  }, [document.responseSets, responseSetId]);

  // Fetch global response set via Apollo (only when the content doesn't
  // carry it)
  const skipGlobal =
    !responseSetId || isLocalResponseSetId(responseSetId) || !!localSet;
  const { data: globalData, loading } = useQuery(DOCUMENT_RESPONSE_SET_QUERY, {
    variables: { id: responseSetId },
    skip: skipGlobal,
  });

  const options: ResponseOption[] = useMemo(() => {
    if (localSet) return localSet.responses;
    if (globalData?.documentResponseSet?.responses) {
      return globalData.documentResponseSet.responses;
    }
    return [];
  }, [localSet, globalData]);

  // Presentation order: randomised per respondent at fill time when
  // shuffleOptions is set. The editor preview keeps the authored order so
  // designers see the canonical list. Shuffle is purely visual — answers are
  // stored by option name, so scoring/results are unaffected.
  const displayOptions: ResponseOption[] = useMemo(() => {
    if (!shuffleOptions || isEditor) return options;
    return seededShuffle(options, shuffleSeed);
  }, [options, shuffleOptions, isEditor, shuffleSeed]);

  const allowOther: boolean = useMemo(() => {
    if (localSet) return !!localSet.allowOther;
    if (globalData?.documentResponseSet?.allowOther !== undefined) {
      return !!globalData.documentResponseSet.allowOther;
    }
    return false;
  }, [localSet, globalData]);

  const allowNone: boolean = useMemo(() => {
    if (localSet) return !!localSet.allowNone;
    if (globalData?.documentResponseSet?.allowNone !== undefined) {
      return !!globalData.documentResponseSet.allowNone;
    }
    return false;
  }, [localSet, globalData]);

  // None pill is only meaningful in multi-select mode.
  const showNonePill = allowNone && allowMultiple;

  const otherSelected = selectedValues.includes(OTHER_VALUE);
  const noneSelected = selectedValues.includes(NONE_VALUE);

  // Picks that count toward the cap (None is "no selection", so it's excluded).
  const selectedRealCount = selectedValues.filter(
    (v) => v !== NONE_VALUE,
  ).length;
  const atMax = maximum !== null && selectedRealCount >= maximum;

  // Look up a response by name to snapshot its score + flagged config.
  const responseByName = useMemo(() => {
    const map = new Map<string, ResponseOption>();
    for (const o of options) map.set(o.name, o);
    return map;
  }, [options]);

  // Persist the canonical value, dropping `other` text when Other
  // isn't selected. Rebuilds the rich array from the canonical view so
  // every entry carries snapshotted score + flagged + sentinel flags.
  const persist = (next: CanonicalValue) => {
    const cleaned: CanonicalValue = {
      selected: next.selected,
      ...(next.selected.includes(OTHER_VALUE) && next.other
        ? { other: next.other }
        : {}),
    };
    const entries: MultiChoiceRichEntry[] = cleaned.selected.map((name) => {
      if (name === OTHER_VALUE) {
        return {
          value: cleaned.other ?? '',
          label: 'Other',
          score: null,
          flagged: null,
          other: true,
          none: false,
        };
      }
      if (name === NONE_VALUE) {
        return {
          value: '',
          label: 'None',
          score: null,
          flagged: null,
          other: false,
          none: true,
        };
      }
      const opt = responseByName.get(name);
      return {
        value: name,
        label: name,
        score:
          opt && typeof opt.score === 'number' && Number.isFinite(opt.score)
            ? opt.score
            : null,
        flagged: opt ? !!(opt as { flagged?: boolean }).flagged : null,
        other: false,
        none: false,
      };
    });
    setValue(entries);
  };

  const handleSingleChange = (next: string) => {
    if (next === '') {
      persist({ selected: [] });
      return;
    }
    persist({
      selected: [next],
      other: next === OTHER_VALUE ? otherText : undefined,
    });
  };

  const handleMultipleChange = (next: string[]) => {
    // Mutual exclusion with None: if the user just added None, drop
    // every other selection (and any 'other' text). If the user added
    // something else while None was previously selected, drop None.
    let normalized = next;
    const previouslyHadNone = selectedValues.includes(NONE_VALUE);
    const nowHasNone = next.includes(NONE_VALUE);
    if (nowHasNone && !previouslyHadNone) {
      // None was just added — clear everything else.
      normalized = [NONE_VALUE];
    } else if (nowHasNone && next.length > 1) {
      // Adding another option alongside None — drop None.
      normalized = next.filter((v) => v !== NONE_VALUE);
    }
    // Enforce the hard cap: reject a pick that would exceed `maximum`. Removals
    // (and selecting None, which clears) always go through.
    if (maximum !== null) {
      const prevReal = selectedValues.filter((v) => v !== NONE_VALUE).length;
      const nextReal = normalized.filter((v) => v !== NONE_VALUE).length;
      if (nextReal > maximum && nextReal > prevReal) return;
    }
    persist({
      selected: normalized,
      other:
        normalized.includes(OTHER_VALUE) && !normalized.includes(NONE_VALUE)
          ? otherText
          : undefined,
    });
  };

  const handleOtherTextChange = (text: string) => {
    persist({ selected: selectedValues, other: text });
  };

  const renderOptions = () => {
    if (!responseSetId) {
      return (
        <p className="text-sm text-muted-foreground">
          No response set selected. Choose one in the element configuration.
        </p>
      );
    }
    if (loading) {
      return <p className="text-sm text-muted-foreground">Loading options…</p>;
    }
    if (options.length === 0 && !allowOther) {
      return (
        <p className="text-sm text-muted-foreground">
          The selected response set has no options.
        </p>
      );
    }

    const toggleOption = (name: string, selected: boolean) => {
      if (isEditor) return;
      if (allowMultiple) {
        handleMultipleChange(
          selected
            ? selectedValues.filter((v) => v !== name)
            : [...selectedValues, name],
        );
      } else {
        handleSingleChange(selected ? '' : name);
      }
    };

    // At the cap, unselected options are disabled at fill time so the limit
    // reads as "these are unavailable" rather than silently ignored.
    const capDisable = (selected: boolean) =>
      !isEditor && allowMultiple && atMax && !selected;

    // A library-style option row: a selected row adopts its option colour
    // (border, light tint bg, coloured check + dark label); an unselected row
    // is neutral with a small colour dot previewing the colour it'll take.
    const renderRow = (
      key: string,
      name: string,
      label: string,
      colour: string,
      selected: boolean,
      disabled: boolean,
    ) => {
      const style: React.CSSProperties = selected
        ? {
            borderColor: colour,
            backgroundColor: `color-mix(in srgb, ${colour} 12%, #fff)`,
            color: `color-mix(in srgb, ${colour} 70%, #000)`,
          }
        : {};
      return (
        <button
          key={key}
          type="button"
          role={allowMultiple ? 'checkbox' : 'radio'}
          aria-checked={selected}
          aria-disabled={disabled || undefined}
          onClick={() => !disabled && toggleOption(name, selected)}
          className={cn(
            'flex w-full items-center gap-[11px] rounded-[10px] px-[13px] py-[11px] text-left transition-colors',
            selected
              ? 'border-[1.5px] font-bold'
              : 'border-[length:var(--forms-input-border-w)] border-[var(--forms-border)] bg-white text-[#3a4252]',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          )}
          style={style}
        >
          <span
            className="flex h-[19px] w-[19px] flex-none items-center justify-center rounded-[6px]"
            style={
              selected
                ? { backgroundColor: colour }
                : { border: '1.6px solid #cdd3db' }
            }
          >
            {selected && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </span>
          <span className="flex-1 text-[14px] leading-[1.4]">{label}</span>
          {!selected && (
            <span
              className="h-[11px] w-[11px] flex-none rounded-full"
              style={{ backgroundColor: colour }}
            />
          )}
        </button>
      );
    };

    return (
      <div className="flex flex-col gap-[8px]">
        {showNonePill &&
          renderRow(NONE_VALUE, NONE_VALUE, 'None', NONE_COLOUR, noneSelected, false)}
        {displayOptions.map((opt) => {
          const selected = selectedValues.includes(opt.name);
          return renderRow(
            opt.name,
            opt.name,
            opt.name,
            opt.colour,
            selected,
            capDisable(selected),
          );
        })}
        {allowOther &&
          renderRow(
            OTHER_VALUE,
            OTHER_VALUE,
            'Other',
            OTHER_COLOUR,
            otherSelected,
            capDisable(otherSelected),
          )}
        {/* Free-form textbox shown only at fill time when "Other" is picked */}
        {allowOther && otherSelected && !isEditor && (
          <Input
            value={otherText}
            onChange={(e) => handleOtherTextChange(e.target.value)}
            placeholder="Please specify…"
            className="max-w-md"
          />
        )}
      </div>
    );
  };

  // Read-only (e.g. a completed result): render the chosen option(s) as
  // static, non-interactive badges — no toggle group, no Other textbox.
  // Prefers the snapshotted rich-entry label so an option renamed or
  // removed after submission still shows its picked text, and keeps the
  // option colour where the source response still exists.
  if (isReadOnly) {
    const readOnlyBadges = richEntries.map((entry, index) => {
      const display = entry.none
        ? 'None'
        : entry.other
        ? entry.value
          ? `Other: ${entry.value}`
          : 'Other'
        : entry.label || entry.value;
      const opt =
        !entry.none && !entry.other
          ? responseByName.get(entry.value)
          : undefined;
      const style: React.CSSProperties = opt?.colour
        ? {
            backgroundColor: opt.colour,
            color: getContrastColor(opt.colour),
            borderColor: opt.colour,
          }
        : {};
      return (
        <span
          key={`${entry.value}-${index}`}
          className={cn(
            'inline-flex items-center rounded-full border px-4 py-1 text-sm font-medium',
            !opt?.colour && 'border-input text-foreground',
          )}
          style={style}
        >
          {display}
        </span>
      );
    });
    return (
      <div className="space-y-3">
        <FieldHeader label={label} subLabel={subLabel} required={required} />
        {readOnlyBadges.length > 0 ? (
          <div className="flex flex-wrap gap-2">{readOnlyBadges}</div>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <FieldLabel required={required}>{label}</FieldLabel>
          {allowMultiple && (minimum !== null || maximum !== null) && (
            <PickCountBadge
              count={selectedRealCount}
              minimum={minimum}
              maximum={maximum}
            />
          )}
        </div>
        <FieldSubLabel>{subLabel}</FieldSubLabel>
      </div>
      {renderOptions()}
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

/**
 * Pill rendered next to the label whenever min and/or max pick counts are
 * configured. Mirrors DropdownSelect's badge: shows the live picked-count,
 * turns destructive below the minimum and amber at the ceiling.
 */
const PickCountBadge: React.FC<{
  count: number;
  minimum: number | null;
  maximum: number | null;
}> = ({ count, minimum, maximum }) => {
  const belowMin = minimum !== null && count < minimum;
  const atMax = maximum !== null && count >= maximum;
  const target =
    minimum !== null && maximum !== null
      ? minimum === maximum
        ? `${minimum}`
        : `${minimum}–${maximum}`
      : minimum !== null
      ? `≥${minimum}`
      : `≤${maximum}`;
  const tone = belowMin
    ? 'border-destructive text-destructive bg-destructive/10'
    : atMax
    ? 'border-amber-500 text-amber-700 bg-amber-50'
    : 'border-border text-muted-foreground bg-muted/40';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium leading-none',
        tone,
      )}
      title={
        minimum !== null && maximum !== null
          ? `Pick ${
              minimum === maximum
                ? `exactly ${minimum}`
                : `between ${minimum} and ${maximum}`
            }`
          : minimum !== null
          ? `Pick at least ${minimum}`
          : `Pick up to ${maximum}`
      }
    >
      {count} / {target}
    </span>
  );
};

export default MultiChoice;
